// Button hover fill slides in from whichever edge the pointer crosses into the button on, and
// back out through whichever edge it crosses out through (Figma node 26:420 usage note: "hover
// should enter from the side you hover on") - independently, so entering left and leaving top
// slides in from the left and back out through the top, not backtracking out the way it came.
// The fill panel (`.button::before`, see atoms/button.css) is positioned entirely via the
// --button-hover-x/--button-hover-y custom properties this module sets - not via a `:hover`
// CSS rule - because the browser matches `:hover` before dispatching the `pointerover` event
// this listens on, so a `:hover { translate: 0 0 }` rule would already be requesting its
// target value before the entry edge is even known, leaving nothing correct to transition from
// on the first hover.
//
// `pointerenter`/`pointerleave` don't bubble, so this listens on `pointerover`/`pointerout` at
// the document root and only acts on the first crossing of a given `.button`'s boundary
// (relatedTarget check), which gives one calculation per hover rather than one per pixel of
// pointer movement.
//
// Touch is ignored: a tap fires emulated hover events, which left the fill stuck on a tapped
// button with no "leave" to follow. Checked per event (pointerType) rather than with a
// `(hover: hover)` media query, so a touchscreen laptop still gets hover from its mouse while
// its taps don't. CSS :hover rules on button classes use `@media (hover: hover)` for the same
// reason (atoms/button.css, layout/block-press-release-list-items.css).
//
// Also drives .card-link (Block Card Links): the whole card is the hover trigger - so the entry
// edge is computed from the CARD's rect, not the small arrow box - while the fill itself lives on
// .card-link__arrow::before. That works unchanged because everything here is set as inherited
// custom properties / a class on the trigger element, which the arrow's ::before reads from its
// ancestor card (see layout/block-card-links.css). Block News & Press List's cards work the same
// way (.block-news-and-press-list__card / __arrow, layout/block-news-and-press-list.css).
// A .card-link--static (card without a link, no arrow) isn't a trigger. Company Timeline's linked
// rows (.block-company-timeline__company--link) and Press Release List Items' rows
// (.block-press-release-list-items__row) carry their fill on their own ::before, like a .button,
// but without its box (layout/block-company-timeline.css, layout/block-press-release-list-items.css).

const HOVER_TRIGGER = ".button, .card-link:not(.card-link--static), .block-news-and-press-list__card, .block-company-timeline__company--link, .block-press-release-list-items__row";

export type Edge = "left" | "right" | "top" | "bottom";

// The resting (hidden) fill sits a couple of pixels past the edge rather than exactly on it:
// at exactly ±100% the fill's edge lands on the button's own edge, and at fractional button
// sizes/positions (resize, scroll) the browser rounds that shared edge into the button's last
// pixel row/column, so a sliver of the fill shows at rest. The CSS resting defaults
// (atoms/button.css etc.) use the same calc().
const EDGE_OFFSET: Record<Edge, [x: string, y: string]> = {
    left: ["calc(-100% - 2px)", "0"],
    right: ["calc(100% + 2px)", "0"],
    top: ["0", "calc(-100% - 2px)"],
    bottom: ["0", "calc(100% + 2px)"],
};

const VISIBLE: [x: string, y: string] = ["0", "0"];

const NO_TRANSITION_CLASS = "button--no-hover-transition";

// Bumped on every handleEnter/handleLeave call for a button, and captured by the rAF callback
// queued in handleEnter. Rapid in/out cycling (e.g. quickly wiggling the mouse across the edge
// several times) queues one rAF per handleEnter call; each is unconditional by itself, so a
// queued "become VISIBLE" callback from an EARLIER hover could still fire and overwrite a MORE
// RECENT handleLeave's exit offset if that hover-out happened after the rAF was queued but
// before it ran - confirmed live: fast enough in/out cycling left the fill stuck fully visible
// with the pointer genuinely outside the button and no further events coming. Comparing the
// token captured at queue time against the button's current token when the frame actually runs
// makes a stale "become visible" callback a no-op instead of clobbering whatever happened since.
const hoverTokens = new WeakMap<HTMLElement, number>();

// Buttons whose fill came from keyboard focus, so focusout only takes back what focusin did.
const focusFilled = new WeakSet<HTMLElement>();

function nextToken(button: HTMLElement): number {
    const token = (hoverTokens.get(button) ?? 0) + 1;
    hoverTokens.set(button, token);
    return token;
}

function nearestEdge(rect: DOMRect, clientX: number, clientY: number): Edge {
    const distances: Record<Edge, number> = {
        left: clientX - rect.left,
        right: rect.right - clientX,
        top: clientY - rect.top,
        bottom: rect.bottom - clientY,
    };

    return (Object.keys(distances) as Edge[]).reduce((closest, edge) =>
        distances[edge] < distances[closest] ? edge : closest,
    );
}

function setOffset(button: HTMLElement, [x, y]: [string, string]): void {
    button.style.setProperty("--button-hover-x", x);
    button.style.setProperty("--button-hover-y", y);
}

// Text color follows the fill, not the pseudo-class: see atoms/button.css `.button` for why
// (light-on-light text on .button--alt during the gap between :focus-visible/:hover matching
// and the fill actually arriving). --button-hover-text-target is redeclared per variant in
// CSS, so this doesn't need to know which variant `button` is.
function setHoverTextActive(button: HTMLElement, active: boolean): void {
    if (active) {
        button.style.setProperty("--button-hover-text", "var(--button-hover-text-target)");
    } else {
        button.style.removeProperty("--button-hover-text");
    }
}

// slideFillIn/slideFillOut are the hover mechanics below, exported so a component can drive the
// same slide-in fill from something other than hover - e.g. Block Map's continent list, which
// slides a selection fill in/out on click (block-map.ts). Any element whose ::before reads
// --button-hover-x/y (and whose text reads --button-hover-text) works, no .button class needed.
export function slideFillIn(button: HTMLElement, edge: Edge): void {
    handleEnter(button, edge);
}

export function slideFillOut(button: HTMLElement, edge: Edge): void {
    // Invalidates any still-queued "become VISIBLE" rAF - see hoverTokens above.
    nextToken(button);
    setOffset(button, EDGE_OFFSET[edge]);
    setHoverTextActive(button, false);
}

function handleEnter(button: HTMLElement, edge: Edge): void {
    // Always snap to the new edge's start offset with transitions off first, THEN let the rAF
    // below animate from there to VISIBLE - never skip this, even on a button that's been
    // hovered before. --button-hover-x/y can be sitting at a DIFFERENT edge's settled offset
    // (e.g. "right: 100%" from the last hover-out), and without forcing that edge write to
    // actually paint before the rAF below overwrites it with VISIBLE, the browser coalesces
    // both writes into one frame and transitions straight from the stale old-edge position to
    // VISIBLE - which looks like the fill sliding in from whichever edge was last used, not the
    // edge this hover actually entered from (confirmed live: exit right, fully settle, enter
    // left -> fill visibly slid in from the right instead of snapping to the left first).
    button.classList.add(NO_TRANSITION_CLASS);
    setOffset(button, EDGE_OFFSET[edge]);
    setHoverTextActive(button, false);
    void button.offsetWidth;
    button.classList.remove(NO_TRANSITION_CLASS);

    const token = nextToken(button);
    requestAnimationFrame(() => {
        if (hoverTokens.get(button) !== token) {
            // A more recent handleEnter/handleLeave happened since this frame was queued -
            // that call owns the button's current state now, this one no-ops.
            return;
        }
        setOffset(button, VISIBLE);
        setHoverTextActive(button, true);
    });
}

function handleLeave(button: HTMLElement, clientX: number, clientY: number): void {
    // Transitions are already enabled and the fill is at "visible" - slideFillOut just moves
    // the target, and it animates out toward the exit edge normally.
    const rect = button.getBoundingClientRect();
    slideFillOut(button, nearestEdge(rect, clientX, clientY));
}

// True if `event` crosses the boundary of `button` (entering or leaving it), as opposed to
// just moving between two descendants both still inside (or both still outside) it.
function crossesBoundary(event: MouseEvent, button: HTMLElement): boolean {
    const relatedTarget = event.relatedTarget as Node | null;
    return !relatedTarget || !button.contains(relatedTarget);
}

// Mouse and pen can hover; touch can't (see the header comment).
function canHover(event: PointerEvent): boolean {
    return event.pointerType !== "touch";
}

export function initButtonHover(): void {
    document.addEventListener("pointerover", (event) => {
        if (!canHover(event)) {
            return;
        }
        const button = (event.target as HTMLElement).closest<HTMLElement>(HOVER_TRIGGER);
        if (button && crossesBoundary(event, button)) {
            const rect = button.getBoundingClientRect();
            handleEnter(button, nearestEdge(rect, event.clientX, event.clientY));
        }
    });

    document.addEventListener("pointerout", (event) => {
        if (!canHover(event)) {
            return;
        }
        const button = (event.target as HTMLElement).closest<HTMLElement>(HOVER_TRIGGER);
        if (button && crossesBoundary(event, button)) {
            handleLeave(button, event.clientX, event.clientY);
        }
    });

    // Keyboard focus (Tab) has no pointer position to derive a direction from - always enter
    // from the left, matching reading order. Only :focus-visible: a mouse click or tap also
    // focuses the button, and filling on that restarted the hover fill on every click (and left
    // it stuck after a tap).
    document.addEventListener("focusin", (event) => {
        const button = (event.target as HTMLElement).closest<HTMLElement>(HOVER_TRIGGER);
        if (button && (event.target as HTMLElement).matches(":focus-visible")) {
            focusFilled.add(button);
            handleEnter(button, "left");
        }
    });

    document.addEventListener("focusout", (event) => {
        const button = (event.target as HTMLElement).closest<HTMLElement>(HOVER_TRIGGER);
        if (button && focusFilled.delete(button) && !button.matches(":hover")) {
            slideFillOut(button, "left");
        }
    });
}
